import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, Row, Col, Button } from 'react-bootstrap';
import {
  reduxForm,
  Field,
  formValueSelector,
  getFormSyncErrors,
} from 'redux-form';
import { FormattedMessage } from 'react-intl';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { renderDateField } from '../../utils/redux-form-helper';
import {
  CORRECTION_BODY_STYLE,
  IMPACTS_SCROLL_STYLE,
} from '../EditMoveDateModal/correctionImpacts';

import validate, { isFutureDate } from './validate';
import messages from './messages';
import { usePropertyMoveInCorrectionPreview } from './hooks';
import PropertyMoveInCorrectionImpacts, {
  PROPERTY_MOVE_IN_IMPACTS_COUNT_ID,
} from './PropertyMoveInCorrectionImpacts';

const FORM = 'editPropertyMoveInDateForm';

const isSelectableDate = (current: Object) => !isFutureDate(current);

type Props = {
  show: boolean,
  dismiss: Function,
  handleSubmit: Function,
  onSubmit: Function,
  pristine: boolean,
  valid: boolean,
  submitting: boolean,
  reset: Function,
  clearSubmitErrors: Function,
  dirty: boolean,
  initialValues: Object,
  isHouseholdMiMoDateChangesEnabled?: boolean,
  // Date correction preview (household-mi-mo-date-changes) only.
  residentId?: string,
  organizationId?: string,
  propertyId?: string,
  /** The Property Move-In Date being corrected, YYYY-MM-DD. */
  currentPropertyMoveInDate?: ?string,
  proposedPropertyMoveInDate?: Object,
  hasSyncErrors?: boolean,
};

const EditPropertyMoveInDateModal = (props: Props) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const correctionFlagOn = Boolean(props.isHouseholdMiMoDateChangesEnabled);
  const { onPreviewStatusChange, canSave, hasImpactList } =
    usePropertyMoveInCorrectionPreview({
      proposedDate: props.proposedPropertyMoveInDate,
      pristine: props.pristine,
      hasSyncErrors: Boolean(props.hasSyncErrors),
      submitting: props.submitting,
    });

  const cancelConfirmationToggle = (visible: boolean) => {
    setShowCancelConfirmation(visible);
  };

  // A server error from a save that settles after the modal was already closed (e.g. Cancel
  // confirmed mid-save) still lands in redux-form's store, which persists independently of this
  // component. Clearing on every open — not only on close — guarantees it never lingers into the
  // next time the user opens this modal.
  useEffect(() => {
    if (props.show && props.isHouseholdMiMoDateChangesEnabled) {
      props.clearSubmitErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  const onClose = () => {
    const {
      dirty,
      reset,
      dismiss,
      clearSubmitErrors,
      isHouseholdMiMoDateChangesEnabled,
    } = props;

    if (dirty && !showCancelConfirmation) {
      cancelConfirmationToggle(true);
      return;
    } else if (dirty && showCancelConfirmation) {
      reset();
    }

    // A server error from a failed save must not linger when reopened.
    if (isHouseholdMiMoDateChangesEnabled) {
      clearSubmitErrors();
    }

    cancelConfirmationToggle(false);
    dismiss();
  };

  const renderModalHeader = () => {
    return (
      <Modal.Header closeButton>
        <i className="icon et-pencil" />
        <Modal.Title componentClass="h1">
          Edit Property Move-In Date
        </Modal.Title>
      </Modal.Header>
    );
  };

  const renderModalFooter = () => {
    const { pristine, valid, submitting, handleSubmit } = props;
    // Under the flag Save waits for the correction preview of the date in the field.
    const submitEnabled = correctionFlagOn
      ? canSave
      : !submitting && (pristine || (!pristine && valid));

    return (
      <Modal.Footer className={showCancelConfirmation ? 'disabled' : ''}>
        <Row>
          <Col xs={6}>
            <Button bsStyle="default" className="pull-right" onClick={onClose}>
              Cancel
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              bsStyle="primary"
              className="pull-left"
              type="submit"
              onClick={handleSubmit}
              disabled={!submitEnabled}
            >
              Save Changes
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    );
  };

  const renderCurrentDate = () => {
    const { currentPropertyMoveInDate } = props;
    if (!correctionFlagOn || !currentPropertyMoveInDate) {
      return null;
    }
    return (
      <Typography
        variant="bodySmall"
        color="text.secondary"
        component="div"
        data-test="pmi-current-date"
      >
        <FormattedMessage
          {...messages.currentDate}
          values={{
            date: formatDateDisplayUTC(currentPropertyMoveInDate, 'MM/DD/YYYY'),
          }}
        />
      </Typography>
    );
  };

  // The one scrolling box of the flag-on modal: only the affected-record list scrolls (see
  // EditMoveDateModal). Its rows hold no focusable control, so while a list is shown the box itself
  // takes focus, named by the record count, for keyboard users to scroll it.
  const renderImpacts = () => {
    if (!correctionFlagOn) {
      return null;
    }
    const {
      residentId,
      organizationId,
      propertyId,
      currentPropertyMoveInDate,
      proposedPropertyMoveInDate,
      hasSyncErrors,
    } = props;
    return (
      <div
        data-test="pmi-impacts-scroll"
        {...(hasImpactList
          ? {
              tabIndex: 0,
              role: 'region',
              'aria-labelledby': PROPERTY_MOVE_IN_IMPACTS_COUNT_ID,
            }
          : {})}
        style={IMPACTS_SCROLL_STYLE}
      >
        <PropertyMoveInCorrectionImpacts
          residentId={residentId ?? ''}
          organizationId={organizationId ?? ''}
          propertyId={propertyId ?? ''}
          proposedDate={proposedPropertyMoveInDate}
          currentPropertyMoveInDate={currentPropertyMoveInDate}
          clientValid={!hasSyncErrors}
          onStatusChange={onPreviewStatusChange}
        />
      </div>
    );
  };

  const renderModalFormBody = () => {
    return (
      <Row className="scrollable-modal__content">
        <div className="container-fluid">
          <Row>
            <Col md={5} xs={12}>
              <label>Property Move-In Date</label>
              {renderCurrentDate()}
            </Col>
            <Col md={7} xs={12}>
              <Field
                name="editPropertyMoveInDate"
                component={renderDateField}
                bsSize="md"
                {...(correctionFlagOn ? { isValidDate: isSelectableDate } : {})}
              />
            </Col>
          </Row>
          {renderImpacts()}
        </div>
      </Row>
    );
  };

  const renderModalCancelBody = () => {
    return (
      <div className="modal-confirm">
        <Row>
          <Col xs={12}>
            <h1>Are you sure you want to leave without saving?</h1>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6}>
            <Button bsStyle="primary" className="pull-right" onClick={onClose}>
              Yes
            </Button>
          </Col>
          <Col xs={12} sm={6}>
            <Button
              bsStyle="default"
              className="pull-left"
              onClick={() => cancelConfirmationToggle(false)}
            >
              No
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  const { show } = props;
  return (
    <form>
      <Modal
        className="edit-move-in-date"
        bsSize="sm"
        // The app's medium modal (et-custom.css .modal-sm.modal-md) gives the impact rows room
        // next to their chips.
        dialogClassName={correctionFlagOn ? 'modal-md' : undefined}
        backdrop
        show={show}
        onHide={() => onClose()}
      >
        {renderModalHeader()}
        <Modal.Body
          style={correctionFlagOn ? CORRECTION_BODY_STYLE : undefined}
        >
          {renderModalFormBody()}
          {showCancelConfirmation && renderModalCancelBody()}
        </Modal.Body>
        {renderModalFooter()}
      </Modal>
    </form>
  );
};

const selectPropertyMoveInDate = formValueSelector(FORM);
const selectSyncErrors = getFormSyncErrors(FORM);

// The date in the field and whether it passes the form's rules, for the correction preview.
export const mapStateToProps = (
  state: Object,
  { initialValues }: Object,
): Object => {
  return {
    initialValues,
    proposedPropertyMoveInDate: selectPropertyMoveInDate(
      state,
      'editPropertyMoveInDate',
    ),
    hasSyncErrors: Boolean(selectSyncErrors(state)?.editPropertyMoveInDate),
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: FORM,
    enableReinitialize: true,
    validate,
  })(EditPropertyMoveInDateModal),
);
